import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEventStore } from '../store/eventStore';
import { useAuthStore } from '../store/authStore';
import { formatElapsed } from '../services/elapsed';
import { apiClient } from '../services/api';
import { RESOURCE_POLL_MS } from '../services/polling';
import {
  Artifact, AttendanceReport, ChatMessage, ChatSettings, Session,
  GuestAttendee, EventParticipant,
} from '../types';
import toast from 'react-hot-toast';
import { ShareEventDialog } from '../components/ShareEventDialog';
import { PhotoUploads } from '../organizer/Photos';
import { RoomQuestions } from '../organizer/RoomQuestions';
import { KindChip, kindOf } from '../organizer/filesAndSummaries/shared';
import { RoomAgenda } from '../organizer/RoomAgenda';
import { RoomBarButton, RoomCard, RoomPortrait, SidePanelHead } from './roomChrome';
import { OrganizerProvider } from '../organizer/i18n';
import { RequestButton, RequestRow } from '../organizer/RequestCard';
import { ensureSingleLiveEvent } from '../organizer/ensureSingleLiveEvent';
import { API_BASE_URL } from '../services/apiConfig';


/** The only things allowed to sit beside the room. */
type SidePanelId = 'resources' | 'questions' | 'participants' | 'requests';

const formatFileSize = (bytes?: number | null): string => {
  if (!bytes) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size < 10 && unit > 0 ? size.toFixed(1) : Math.round(size)} ${units[unit]}`;
};

const EventRoomInner: React.FC = () => {
  const { eventCode } = useParams<{ eventCode: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { currentEvent, setCurrentEvent, participants, setParticipants, transcript, addTranscriptSegment } = useEventStore();
  const wsRef = useRef<WebSocket | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const [resources, setResources] = useState<Artifact[]>([]);
  const [deletingResource, setDeletingResource] = useState<string | null>(null);
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // What people write, which is only ever an offer for the board now.
  const [showChat] = useState(false);
  const [chatSettings, setChatSettings] = useState<ChatSettings>({
    chat_enabled: false,
    direct_messages_enabled: false,
  });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [pending, setPending] = useState<ChatMessage[]>([]);
  const [waitingGuests, setWaitingGuests] = useState<GuestAttendee[]>([]);
  const [decidingGuest, setDecidingGuest] = useState<string | null>(null);
  const [showShare, setShowShare] = useState(false);
  const [showEndChoice, setShowEndChoice] = useState(false);
  const [roomGuests, setRoomGuests] = useState<GuestAttendee[]>([]);
  /** Which half of the participants panel is being read. */
  const [peopleTab, setPeopleTab] = useState<'here' | 'attendance'>('here');
  const [requestTab, setRequestTab] = useState<'room' | 'board'>('room');
  /**
   * What has arrived while nobody was looking at it.
   *
   * A event runs while you are reading something else in it: a question
   * is asked while the resources are open, a file is shared while you are
   * in the chat. The count on each control is how many of those have piled
   * up, and opening that panel is what clears it - not a timer, and not
   * the next thing that happens to arrive.
   */
  const [unseen, setUnseen] = useState({ questions: 0, resources: 0 });
  /**
   * What the bar has been asked to put beside the room, in the order it
   * was asked for. First clicked sits at the top; clicking it again takes
   * it away. Nothing but these three may occupy that column, so the
   * transcript keeps the width until somebody actually wants something
   * there.
   */
  const [side, setSide] = useState<SidePanelId[]>([]);
  const [resourceTab, setResourceTab] = useState<'slides' | 'photos'>('slides');
  /**
   * Two at a time, and the third pushes out the first.
   *
   * A queue with a length. Stacking all three left each one a letterbox
   * with a scrollbar - a chat you cannot read is not a chat - so the
   * column holds two, and asking for another lets the one that has been
   * there longest go. Which is the same rule the order already follows:
   * first in, first out.
   */
  const ROOM_BESIDE = 2;
  const toggleSide = (panel: SidePanelId) =>
    setSide((open) => {
      if (open.includes(panel)) return open.filter((x) => x !== panel);
      return [...open, panel].slice(-ROOM_BESIDE);
    });
  const closeSide = (panel: SidePanelId) =>
    setSide((open) => open.filter((x) => x !== panel));

  /** The event's running order, for the agenda down the left. */
  const [agenda, setAgenda] = useState<Session[]>([]);
  const [attendance, setAttendance] = useState<AttendanceReport | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const showChatRef = useRef(false);
  /** What is beside the room, read inside the socket handler. */
  const sideRef = useRef<SidePanelId[]>([]);
  // Read inside the socket handler, which is created once.
  const showAttendanceRef = useRef(false);
  /** The reloads the socket asks for, kept current without rebuilding it. */
  /** Something arrived for a panel nobody has open. */
  const noteUnseen = (which: 'questions' | 'resources', by = 1) => {
    if (sideRef.current.includes(which)) return;
    setUnseen((was) => ({ ...was, [which]: was[which] + by }));
  };

  const refreshRef = useRef({
    roster: () => {},
    resources: () => {},
    attendance: () => {},
    event: () => {},
    agenda: () => {},
  });
  const eventIdRef = useRef<string | null>(null);
  const elapsedRef = useRef(0);

  // Effects key off these primitives rather than the event object, whose
  // identity changes on every refetch and would otherwise tear down the
  // websocket, the camera and the timer.
  const eventId = currentEvent?.id ?? null;
  const session = currentEvent?.current_session ?? null;
  // The room clock follows the current agenda's server-owned start time.
  const startedAt = session?.started_at ?? null;

  useEffect(() => {
    eventIdRef.current = eventId;
  }, [eventId]);

  /**
   * What the finished talks settled, where it has been written up.
   *
   * Read once on the way in and again whenever the running order moves:
   * a summary is published from the desk after a talk, so it appears
   * between one session and the next rather than second by second.
   */
  const [summaries, setSummaries] = useState<Record<string, any>>({});
  const refreshSummaries = useCallback(async () => {
    const id = eventIdRef.current;
    if (!id) return;
    try {
      const { conclusions } = await apiClient.getConclusions();
      const mine: Record<string, any> = {};
      conclusions
        .filter((one) => one.event_id === id)
        .forEach((one) => { mine[one.session_id] = one; });
      setSummaries(mine);
    } catch {
      // Nothing published, or not this reader's to see.
    }
  }, []);

  /** Re-read the running order. The host moves it; the room follows. */
  const refreshAgenda = useCallback(async () => {
    const id = eventIdRef.current;
    if (!id) return;
    try {
      setAgenda(await apiClient.listSessions(id));
    } catch {
      // A dropped refresh is not worth interrupting the event for.
    }
  }, []);

  useEffect(() => {
    if (!eventId) { setAgenda([]); return; }
    apiClient.listSessions(eventId).then(setAgenda).catch(() => setAgenda([]));
    refreshSummaries();
  }, [eventId, refreshSummaries]);

  /** Refresh participants only - no spinner, no event object churn. */
  const refreshParticipants = useCallback(async () => {
    const id = eventIdRef.current;
    if (!id) return;
    try {
      setParticipants(await apiClient.getParticipants(id));
    } catch {
      // A dropped refresh is not worth interrupting the event for.
    }
  }, [setParticipants]);

  useEffect(() => {
    sideRef.current = side;
    showChatRef.current = showChat;
    // The register is only re-read for somebody actually looking at it.
    showAttendanceRef.current =
      side.includes('participants') && peopleTab === 'attendance';
    // Opening a panel is reading it.
    setUnseen((was) => ({
      questions: side.includes('questions') ? 0 : was.questions,
      resources: side.includes('resources') ? 0 : was.resources,
    }));
  }, [showChat, side, peopleTab]);

  const isHost = !!user && currentEvent?.host?.id === user.id;

  /** Whoever runs the room: the host, or anyone helping run it. */
  const canOrganize =
    isHost ||
    (participants as EventParticipant[]).some(
      (p) => p.user?.id === user?.id && ['host', 'co_host'].includes(p.role)
    );
  const boardRequests = pending.filter(
    (message) => message.topic === 'faq' || message.topic === 'suggestion'
  );
  /*
   * Every message in the room is written to somebody.
   *
   * There is no room-wide thread any more: what people have to say goes
   * to the host or to the speaker, and the host decides what to do with
   * it - which is the moderation queue that was already there. Anything
   * left over from a event that had a public thread stays out of the
   * way rather than appearing with nobody to have received it.
   */

  /**
   * Who can be written to privately.
   *
   * Attendees may write to whoever is running the room. Whoever is running
   * it may write back to anybody in it, guests included - a reply you
   * cannot send is not a conversation, and until now the guest side of a
   * private thread simply had no return path: guests are not participants,
   * so they never appeared in this list at all.
   */
  const organizers = (participants as EventParticipant[]).filter(
    (p) => p.user?.id !== user?.id && ['host', 'co_host', 'presenter'].includes(p.role)
  );
  const others = (participants as EventParticipant[]).filter(
    (p) => p.user?.id !== user?.id && !['host', 'co_host', 'presenter'].includes(p.role)
  );

  const canReplyToAnyone = isHost;
  /*
   * One entry per person, however many lists they turn up in.
   *
   * A guest arrives twice: the roster projects them as a participant with
   * the guest's own id, and the guest list carries them again under the
   * same id. Both are the same seat, and showing it twice put the same
   * face in the chat list over and over.
   */
  const dmTargets: { id: string; label: string }[] = [];
  const alreadyListed = new Set<string>();
  const offer = (id: string, label: string) => {
    if (!id || id === user?.id || alreadyListed.has(id)) return;
    alreadyListed.add(id);
    dmTargets.push({ id, label });
  };

  organizers.forEach((p) =>
    offer(p.user.id, `${p.user.email} (${p.role.replace('_', '-')})`)
  );
  if (canReplyToAnyone) {
    others.forEach((p) => offer(p.user.id, p.user.email));
    roomGuests.forEach((g) => offer(g.id, `${g.full_name} (guest)`));
  }

  const loadChat = useCallback(async () => {
    const id = eventIdRef.current;
    if (!id) return;
    try {
      const settings = await apiClient.getChatSettings(id);
      setChatSettings(settings);
      if (settings.chat_enabled) {
        setMessages(await apiClient.getChatMessages(id));
      } else {
        setMessages([]);
      }
    } catch {
      // Leave chat closed; the panel explains it needs enabling.
    }
  }, []);

  const loadPending = useCallback(async () => {
    const id = eventIdRef.current;
    if (!id) return;
    try {
      setPending(await apiClient.getPendingMessages(id));
    } catch {
      // Not the host, or chat closed - nothing to show.
    }
  }, []);

  const loadGuests = useCallback(async () => {
    const id = eventIdRef.current;
    if (!id) return;
    try {
      const all = await apiClient.getGuests(id);
      setWaitingGuests(all.filter((g) => g.status === 'pending'));
      // The ones already in the room are who the host can write back to.
      setRoomGuests(all.filter((g) => g.status === 'admitted'));
    } catch {
      // Only the host may read this.
    }
  }, []);

  const decideGuest = async (guestId: string, decision: 'admit' | 'deny') => {
    const id = eventIdRef.current;
    if (!id) return;
    try {
      setDecidingGuest(guestId);
      await apiClient.admitGuest(id, guestId, decision);
      setWaitingGuests((prev) => prev.filter((g) => g.id !== guestId));
      toast.success(decision === 'admit' ? 'Guest admitted' : 'Guest denied');
    } catch (error: any) {
      toast.error(error.response?.data?.error ?? 'Could not update guest');
    } finally {
      setDecidingGuest(null);
    }
  };

  const decideBoardRequest = async (
    message: ChatMessage, decision: 'approve' | 'decline'
  ) => {
    if (!eventId) return;
    try {
      setDecidingGuest(message.id);
      await apiClient.moderateMessage(
        eventId,
        message.id,
        decision,
        decision === 'approve'
          ? (message.topic === 'suggestion' ? 'suggestion' : 'faq')
          : undefined
      );
      setPending((previous) => previous.filter((item) => item.id !== message.id));
      toast.success(decision === 'approve' ? 'Request accepted' : 'Request rejected');
    } catch (error: any) {
      toast.error(error.response?.data?.error ?? 'Could not process request');
    } finally {
      setDecidingGuest(null);
    }
  };

  const loadAttendance = useCallback(async () => {
    const id = eventIdRef.current;
    if (!id) return;
    try {
      setAttendance(await apiClient.getAttendance(id));
    } catch {
      toast.error('Could not load attendance');
    }
  }, []);

  /**
   * Tell the room this person is still here.
   *
   * Presence is measured by what somebody does, not by whether their
   * socket is open - a tab left running in a window nobody is looking at
   * holds one all afternoon. So moving, typing or coming back to the tab
   * says so, throttled to every fifteen seconds because mouse events fire
   * hundreds of times.
   */
  const lastBeat = useRef(0);
  const beat = useCallback(() => {
    const now = Date.now();
    if (now - lastBeat.current < 15000) return;
    const socket = wsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    lastBeat.current = now;
    socket.send(JSON.stringify({ type: 'heartbeat' }));
  }, []);

  /** Held in a ref so opening the socket does not depend on it. */
  const beatRef = useRef(beat);
  useEffect(() => { beatRef.current = beat; }, [beat]);

  useEffect(() => {
    const wake = () => beat();
    const watched: (keyof WindowEventMap)[] = [
      'pointerdown', 'keydown', 'wheel', 'touchstart', 'focus',
    ];
    watched.forEach((name) => window.addEventListener(name, wake));
    // Coming back to a tab is the clearest sign of all that somebody is
    // there, and it is the one case a mousemove may never follow.
    document.addEventListener('visibilitychange', wake);
    return () => {
      watched.forEach((name) => window.removeEventListener(name, wake));
      document.removeEventListener('visibilitychange', wake);
    };
  }, [beat]);

  const sendTo = (
    to: string, body: string, topic: 'faq' | 'suggestion' | null = null
  ) => {
    if (!body || !to) return;

    const socket = wsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      toast.error('Not connected to the event');
      return;
    }

    socket.send(JSON.stringify({
      type: 'chat_message',
      message: body,
      recipient_id: to,
      // Which board it was written under. The host decides whether it
      // goes up; they should not also have to decide what it is.
      topic,
    }));
  };

  /**
   * Put something to whoever runs the room.
   *
   * There is no chat any more, so there is only one thing a message can
   * be: something offered for the board. It goes to the host over the
   * same socket frame as before and waits in their requests until they
   * put it up or decline it.
   */
  const askHost = (body: string, topic: 'faq' | 'suggestion') => {
    const host = organizers.find((p) => p.role === 'host') ?? organizers[0];
    if (!host?.user?.id) {
      toast.error('Nobody is here to receive it yet');
      return;
    }
    sendTo(host.user.id, body, topic);
  };

  /**
   * Re-read the event without the ceremony of arriving at it.
   *
   * ``loadEvent`` puts a spinner up, joins the room and checks the door,
   * all of which is right on the way in and wrong for a socket saying the
   * running order moved on.
   */
  const refreshEvent = useCallback(async () => {
    const id = eventIdRef.current;
    if (!id || !eventCode) return;
    try {
      setCurrentEvent(await apiClient.getEvent(eventCode));
    } catch {
      // A dropped refresh is not worth interrupting the event for.
    }
  }, [eventCode, setCurrentEvent]);

  const loadEvent = useCallback(async () => {
    try {
      setIsLoading(true);
      const event = await apiClient.getEvent(eventCode!);
      setCurrentEvent(event);

      // The room opens a quarter of an hour before its hour. Coming
      // earlier is not an error to shout about - say when to come back.
      if (event.entry && !event.entry.is_open) {
        const opens = new Date(event.entry.opens_at);
        toast(
          `This room opens at ${opens.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}. It is too early to go in.`,
          { icon: '\u23F1\uFE0F', duration: 6000 }
        );
        navigate('/');
        return;
      }

      // Walking into the room is joining it. Without this the page only
      // ever reads: somebody holding a code is not a participant, so every
      // member endpoint - participants, chat settings, files - answers 404
      // and the room comes up empty. Joining also records that they were
      // here, which is what attendance is counted from.
      await apiClient.joinEvent(event.code);

      const participants = await apiClient.getParticipants(event.id);
      setParticipants(participants);
    } catch (error: any) {
      const refusal = error.response?.data;
      if (refusal?.code === 'too_early') {
        toast(refusal.error, { icon: '\u23F1\uFE0F', duration: 6000 });
      } else if (error.response?.status === 404) {
        toast.error('No event with that code.');
      } else {
        toast.error('Failed to load event: ' + error.message);
      }
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  }, [eventCode, navigate, setCurrentEvent, setParticipants]);

  const connectWebSocket = useCallback(() => {
    // The websocket lives on the Django backend, not on the dev server that
    // serves this page, so derive the host from the API URL.
    const apiUrl = new URL(API_BASE_URL);
    const protocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';

    // A websocket handshake cannot carry an Authorization header, so the
    // access token goes in the query string.
    const token = localStorage.getItem('access_token');
    const query = token ? `?token=${encodeURIComponent(token)}` : '';
    const wsUrl = `${protocol}//${apiUrl.host}/ws/event/${eventCode}/${query}`;

    wsRef.current = new WebSocket(wsUrl);
    wsRef.current.onopen = () => {
      console.log('WebSocket connected');
      // Arriving is itself a sign of life, and without this the first
      // stamp waits on the person doing something.
      beatRef.current();
    };

    wsRef.current.onmessage = (message) => {
      const data = JSON.parse(message.data);
      if (data.type === 'transcription_update' && data.segment) {
        addTranscriptSegment(data.segment);
      } else if (data.type === 'state_update' || data.type === 'participant_joined'
                 || data.type === 'participant_left') {
        refreshParticipants();
        // The running order moving on changes which session the room is
        // holding, and a session ended early is over well before the time
        // it was given. Re-read it: whoever is sitting in a session that
        // has finished should be shown the door, and whoever is sitting in
        // one that has just been handed the stage should stay put.
        if (data.state?.session_ended || data.state?.session_started) {
          refreshRef.current.event();
        }
        // The host has rearranged the running order, or a talk running
        // long has moved everything after it. Either way the times on
        // every screen in the room are out of date until this lands.
        if (
          data.state?.schedule_changed
          || data.state?.session_ended
          || data.state?.session_started
        ) {
          refreshRef.current.agenda();
        }
      } else if (data.type === 'chat_message') {
        setMessages((prev) => {
          if (data.message_id && prev.some((m) => m.id === data.message_id)) return prev;
          return [...prev, {
            id: data.message_id ?? `${data.user_id}-${data.timestamp}`,
            body: data.message,
            created_at: data.timestamp,
            is_direct: !!data.is_direct,
            moderation_status: data.moderation_status,
            sender_id: data.user_id,
            sender_name: data.user_name,
            sender_email: null,
            sender_is_guest: !!data.sender_is_guest,
            recipient_id: data.recipient_id ?? null,
            recipient_name: data.recipient_name ?? null,
            recipient_is_guest: !!data.recipient_is_guest,
          }];
        });
      } else if (data.type === 'chat_pending') {
        setPending((prev) =>
          prev.some((m) => m.id === data.message_id) ? prev : [...prev, {
            id: data.message_id,
            body: data.message,
            created_at: data.timestamp,
            is_direct: true,
            moderation_status: 'pending',
            sender_id: data.user_id,
            sender_name: data.user_name,
            sender_email: null,
            sender_is_guest: !!data.sender_is_guest,
            recipient_id: data.recipient_id ?? null,
            recipient_name: data.recipient_name ?? null,
            recipient_is_guest: !!data.recipient_is_guest,
            topic: data.topic ?? 'faq',
            session: data.session_id ?? null,
          }]
        );
      } else if (data.type === 'chat_moderated') {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === data.message_id
              ? { ...m, moderation_status: data.moderation_status }
              : m
          )
        );
        if (data.moderation_status === 'declined') {
          toast(`Host declined your message to ${data.recipient_name ?? 'them'}`, { icon: '🚫' });
        } else if (data.moderation_status === 'approved') {
          toast.success(`Host forwarded your message to ${data.recipient_name ?? 'them'}`);
        }
      } else if (data.type === 'guest_waiting') {
        setWaitingGuests((prev) =>
          prev.some((g) => g.id === data.guest_id) ? prev : [...prev, {
            id: data.guest_id,
            full_name: data.full_name,
            phone: data.phone,
            email: data.email ?? '',
            status: 'pending',
            created_at: data.created_at,
            decided_at: null,
          }]
        );
        toast(`${data.full_name} is asking to join`, { icon: '🔔' });
      } else if (data.type === 'roster_update') {
        // Somebody came in or stepped out. Ask for the list rather than
        // patching it here: the server already knows who is in the room.
        // Read through refs so this handler - and with it the socket - is
        // not rebuilt every time one of them changes.
        refreshRef.current.roster();
      } else if (data.type === 'resources_update') {
        noteUnseen('resources');
        refreshRef.current.resources();
      } else if (data.type === 'attendance_update') {
        if (showAttendanceRef.current) refreshRef.current.attendance();
      } else if (data.type === 'event_started') {
        setCurrentEvent({
          ...(useEventStore.getState().currentEvent as any),
          started_at: data.started_at,
          status: data.status,
        });
      } else if (data.type === 'idle_evicted') {
        // The room has let this person go for going quiet. Said plainly,
        // because otherwise their screen carries on showing a room that
        // no longer counts them and nothing explains why.
        toast('You were away, so the room let you go', { icon: '💤' });
        navigate('/');
      } else if (data.type === 'event_ended') {
        toast(
          data.reason === 'time_elapsed'
            ? 'The event time is over'
            : 'The host ended the event',
          { icon: '👋' }
        );
        navigate('/');
      } else if (data.type === 'chat_settings_update') {
        // The room itself is always open; what the host turns on and off
        // is whether anybody may write in it. Nothing is cleared either
        // way - a conversation that happened still happened.
        setChatSettings({
          chat_enabled: true,
          direct_messages_enabled: data.direct_messages_enabled,
        });
      } else if (data.type === 'chat_error') {
        toast.error(data.error);
      }
    };

    wsRef.current.onerror = (error) => {
      console.error('WebSocket error:', error);
      toast.error('Connection error');
    };
  }, [eventCode, addTranscriptSegment, refreshParticipants, setCurrentEvent, navigate]);

  useEffect(() => {
    if (eventCode) {
      loadEvent();
    }
  }, [eventCode, loadEvent]);

  // Keyed on the event id: re-running this on every event refetch would
  // drop the websocket and restart the camera mid-call.
  useEffect(() => {
    if (!eventId) return;

    connectWebSocket();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [eventId, connectWebSocket]);

  const loadResources = useCallback(async (eventId: string) => {
    try {
      setResources(await apiClient.getResources(eventId));
    } catch {
      // Resources are supplementary; a failure here must not break the room.
    }
  }, []);

  // Keep the socket's reloads pointing at the current functions. The
  // handler holds the ref, not the functions, so the connection survives.
  useEffect(() => {
    refreshRef.current = {
      roster: () => {
        const id = eventIdRef.current;
        if (id) apiClient.getParticipants(id).then(setParticipants).catch(() => undefined);
      },
      resources: () => {
        const id = eventIdRef.current;
        if (id) loadResources(id);
      },
      attendance: () => loadAttendance(),
      event: () => refreshEvent(),
      agenda: () => { refreshAgenda(); refreshSummaries(); },
    };
  }, [
    loadResources, loadAttendance, setParticipants, refreshEvent,
    refreshAgenda, refreshSummaries,
  ]);

  // The device streams new lines over the socket; this fills in what was
  // said before we arrived.
  useEffect(() => {
    if (!eventCode) return;
    apiClient
      .getEventSegments(eventCode)
      .then((segments) => segments.forEach(addTranscriptSegment))
      .catch(() => undefined);
  }, [eventCode, addTranscriptSegment]);

  useEffect(() => {
    if (eventId) loadChat();
  }, [eventId, loadChat]);

  useEffect(() => {
    if (!eventId || !isHost || !chatSettings.chat_enabled) return undefined;
    loadPending();
    const id = window.setInterval(loadPending, 15000);
    return () => window.clearInterval(id);
  }, [eventId, isHost, chatSettings.chat_enabled, loadPending]);

  useEffect(() => {
    if (!eventId || !isHost) return undefined;
    loadGuests();
    const id = window.setInterval(loadGuests, 15000);
    return () => window.clearInterval(id);
  }, [eventId, isHost, loadGuests]);

  // Keep the newest message in view while the panel is open.
  useEffect(() => {
    if (showChat) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, showChat]);

  // Anyone in the event can upload, so poll to pick up other people's
  // files. Also refresh on tab focus, since polling is paused while hidden.
  useEffect(() => {
    if (!eventId) return;

    loadResources(eventId);

    const id = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadResources(eventId);
      }
    }, RESOURCE_POLL_MS);

    const onFocus = () => loadResources(eventId);
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [eventId, loadResources]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentEvent) return;

    setUploadPercent(0);
    try {
      const artifact = await apiClient.uploadResource(
        currentEvent.id,
        file,
        setUploadPercent
      );
      setResources((prev) => [artifact, ...prev]);
      toast.success(`Uploaded ${file.name}`);
    } catch (error: any) {
      const detail = error.response?.data?.error ?? error.message;
      toast.error(`Upload failed: ${detail}`);
    } finally {
      setUploadPercent(null);
      // Allow re-selecting the same file.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleResourceDelete = async (resource: Artifact) => {
    if (!eventId) return;
    setDeletingResource(resource.id);
    try {
      await apiClient.deleteResource(eventId, resource.id);
      setResources((previous) => previous.filter((item) => item.id !== resource.id));
      toast.success(`Removed ${resource.display_name}`);
    } catch (error: any) {
      const detail = error.response?.data?.error ?? error.message;
      toast.error(`Could not remove ${resource.display_name}: ${detail}`);
    } finally {
      setDeletingResource(null);
    }
  };

  /**
   * A session ending does not end the room.
   *
   * The room belongs to the event, not to one talk in it: a event of
   * ten sessions is one room that all ten happen in. When a session comes
   * off stage its transcript, its chat and its resources are closed off
   * and belong to it, and the room goes on - waiting for the host to start
   * the next speaker. Only the event ending shows anybody the door, and
   * that arrives over the socket as ``event_ended``.
   *
   * This is all that is left of the old behaviour: saying so, once, rather
   * than navigating away.
   */
  const lastOnStage = useRef<string | null>(null);
  useEffect(() => {
    const now = session?.id ?? null;
    const before = lastOnStage.current;
    lastOnStage.current = now;
    if (before && !now) {
      toast('That session has finished. The room stays open.', {
        icon: '\u2705',
        duration: 4000,
      });
    }
  }, [session?.id]);

  /** Put the next speaker on stage. The host advances the running order. */
  const [starting, setStarting] = useState(false);
  const startSession = async (sessionId?: string | null) => {
    const nextId = sessionId ?? currentEvent?.current_session?.next_id;
    if (!nextId) return;
    setStarting(true);
    try {
      if (isHost && currentEvent && user?.id) {
        await ensureSingleLiveEvent(currentEvent.id, user.id);
      }
      await apiClient.startSession(nextId);
      await refreshRef.current.event();
      await refreshAgenda();
    } catch (error: any) {
      toast.error(
        error.response?.data?.error
          ?? error.message
          ?? 'Could not start that session'
      );
    } finally {
      setStarting(false);
    }
  };

  // Session timer, anchored to the server's started_at so every participant
  // sees the same count, and leaving and returning resumes rather than resets.
  useEffect(() => {
    if (!eventId || !startedAt) {
      setElapsed(0);
      return;
    }
    const origin = new Date(startedAt).getTime();

    const tick = () => {
      const secs = Math.max(0, Math.floor((Date.now() - origin) / 1000));
      elapsedRef.current = secs;
      setElapsed(secs);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [eventId, startedAt]);

  /**
   * Host only: end it for everyone.
   *
   * Kept apart from leaving, which is the other thing a host might mean.
   * The two are asked about rather than guessed at, because one of them
   * empties the hall.
   */
  const endEvent = async () => {
    if (!currentEvent) return;
    setShowEndChoice(false);
    try {
      await apiClient.endEvent(currentEvent.id);
      toast.success('Event ended');
      navigate('/');
    } catch (error: any) {
      toast.error(
        error.response?.data?.error ?? 'Failed to end event: ' + error.message
      );
    }
  };

  /**
   * End the talk on stage. The room is not ended with it.
   *
   * Its transcript, its chat and its resources are closed off and belong
   * to it; the event carries on and offers the host the next speaker.
   * The other half of the same button, one line down, ends the event -
   * which is a different thing, and worth being asked which you meant.
   */
  const [endingSession, setEndingSession] = useState(false);
  const endSession = async () => {
    const onStage = currentEvent?.current_session?.id;
    if (!onStage) return;
    setEndingSession(true);
    try {
      const result = await apiClient.endSession(onStage);
      if (result.event_ended) {
        navigate('/');
        return;
      }
      setShowEndChoice(false);
      await refreshRef.current.event();
      await refreshAgenda();
      toast.success('Session ended. The room stays open.');
    } catch (error: any) {
      toast.error(error.response?.data?.error ?? 'Could not end that session');
    } finally {
      setEndingSession(false);
    }
  };

  /** Everyone else: step out, event carries on. */
  const leaveEvent = async () => {
    if (!currentEvent) return;
    try {
      await apiClient.leaveEvent(currentEvent.id);
    } catch {
      // Leaving is best-effort; navigate away regardless.
    }
    if (isHost) {
      try {
        sessionStorage.setItem('manch.return_to_organizer', '1');
      } catch {
        // Continue to the dashboard even when session storage is unavailable.
      }
      navigate('/organizer');
    } else {
      navigate('/');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#f1f4f8]">
        <p className="text-[16px] text-[#4a5567]">Loading event…</p>
      </div>
    );
  }

  if (!currentEvent) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#f1f4f8]">
        <p className="text-[16px] text-[#4a5567]">Event not found</p>
      </div>
    );
  }

  const speaker =
    agenda.find((s) => s.id === session?.id)?.speaker_name || '';

  /** Nothing on stage: the room is between talks, not finished. */
  const room = currentEvent.current_session;
  const waiting = !session?.id;
  const nextTitle = room?.next_title || '';

  /*
   * A transcript belongs to the talk it was said during.
   *
   * While a session is on stage the room shows that session's lines, so
   * the next speaker starts on a clean page rather than underneath the
   * last one. Between talks there is nothing being said, and what the
   * room has is the record of the event so far.
   */
  const roomLines = (
    session?.id
      ? transcript.filter(
          (seg: any) => !seg.session_id || seg.session_id === session.id
        )
      : transcript
  )
    // Newest first, in this room only. The host is watching what is
    // being said now, and the newest line arriving at the bottom of a
    // list that is already taller than the panel puts it out of sight
    // - so the one line that matters is the one nobody can see.
    // Everywhere else the transcript is read as a record, from the
    // top, and keeps the order it was said in.
    //
    // Copied before reversing: this is the store's own array, and
    // turning it over in place would turn it over for every screen
    // reading the same state.
    .slice()
    .reverse();

  return (
    <div className="min-h-screen bg-[#f1f4f8] text-[#030712] pb-[110px]">
      {showEndChoice && currentEvent && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4">
          <div className="bg-white text-gray-900 rounded-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold">Leaving, or ending it?</h3>
            <p className="mt-1 text-sm text-gray-600">
              Those are different things, so this asks rather than guesses.
              The talk on stage is ended from the card that shows it.
            </p>

            <button
              onClick={endEvent}
              className="mt-5 w-full text-left rounded-xl border border-red-200 hover:border-red-400 p-4"
            >
              <span className="block font-semibold text-red-700">End the event</span>
              <span className="block text-sm text-gray-600 mt-0.5">
                Everything ends with it. Whatever is on stage is finished,
                anything never opened reads as never started, and the room
                closes for everybody — speakers, attendees and guests.
              </span>
            </button>

            <button
              onClick={() => { setShowEndChoice(false); leaveEvent(); }}
              className="mt-3 w-full text-left rounded-xl border border-navy-800/15 hover:border-navy-800/40 p-4"
            >
              <span className="block font-semibold">Just leave</span>
              <span className="block text-sm text-gray-600 mt-0.5">
                The event carries on without you, and you can come back.
              </span>
            </button>

            <button
              onClick={() => setShowEndChoice(false)}
              className="mt-4 w-full text-center text-sm text-gray-600 hover:text-gray-900"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showShare && currentEvent && (
        <ShareEventDialog
          eventId={currentEvent.id}
          eventCode={currentEvent.code}
          onClose={() => setShowShare(false)}
          onInvited={() => loadAttendance()}
        />
      )}

      {/* The room itself: the running order, the stage, and the side panels */}
      <div
        className={`grid gap-4 p-4 items-start ${
          side.length > 0
            ? 'xl:grid-cols-[398px_minmax(0,1fr)_358px]'
            : 'xl:grid-cols-[398px_minmax(0,1fr)]'
        }`}
      >
        {/* What the day runs through, as it actually stands. The host
            rearranges it from here; everybody else watches it move. */}
        <RoomCard className="xl:sticky xl:top-4">
          <RoomAgenda
            event={currentEvent}
            sessions={agenda}
            liveSessionId={session?.id ?? null}
            canEdit={isHost}
            onChanged={refreshAgenda}
            summaries={summaries}
          />
        </RoomCard>

        {/* The stage */}
        <div className="flex flex-col gap-3 min-w-0">
          <RoomCard>
            <div className="bg-white border-b border-[#e3e8ef] flex items-center justify-between
              gap-3 px-4 py-2.5 flex-wrap">
              <h1 className="flex-1 min-w-0 text-[24px] font-medium text-black text-center
                leading-[1.2] truncate">
                {currentEvent.title}
              </h1>

              <div className="flex items-center gap-2 flex-none">

                {/* Nothing here starts the event. Putting a talk on
                    stage does that - it is the same decision, and asking
                    for it twice only made it possible to be half-started:
                    a event under way with nobody speaking. */}
                {startedAt && (
                  <span className="bg-live text-white text-[12px] tracking-[-0.06px]
                    rounded-[4px] h-6 px-2 grid place-items-center">
                    Live
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-3 px-4 py-2.5">
              <div className="flex gap-2 items-center">
                <RoomPortrait name={speaker || currentEvent.title} size={84} />
                <div className="min-w-0">
                  <p className="text-[22px] font-medium text-black leading-[1.2] truncate">
                    {session?.title || currentEvent.title}
                  </p>
                  <p className="text-[18px] text-[#030712] leading-[1.5] truncate">
                    {speaker || 'No speaker named'}
                  </p>
                </div>
              </div>

              <div className="flex gap-3 items-center flex-wrap">
                <span className="border border-[#e3e8ef] rounded-[4px] h-6 px-2 flex items-center gap-1.5">
                  <i className="w-[5px] h-[5px] rounded-full bg-[#13cef7]" aria-hidden />
                  <span className="text-[14px] text-[#030712] tracking-[-0.07px]">
                    {currentEvent.code}
                  </span>
                </span>
                <span aria-hidden className="w-px h-3 bg-[#e3e8ef]" />
                <span className="text-[14px] text-[#030712] tracking-[-0.07px] tabular-nums">
                  {startedAt ? formatElapsed(elapsed) : 'Not started'}
                </span>

                {/* Ending the talk belongs beside the talk. Ending the
                    event is a different thing and lives behind Leave,
                    where it is asked about rather than sat next to. */}
                {isHost && session?.id && (
                  <button
                    onClick={endSession}
                    disabled={endingSession}
                    className="ms-auto bg-live hover:brightness-110 text-white rounded-[8px]
                      px-4 py-1.5 text-[14px] font-medium disabled:opacity-60"
                  >
                    {endingSession ? 'Ending…' : 'End Session'}
                  </button>
                )}
              </div>

              {/* The room outlives every talk in it. Between two of them it
                  says so, and offers the host the next one. */}
              {waiting && (
                <div className="rounded-[10px] border border-dashed border-[#cfd8e6]
                  bg-[#f8fafc] px-4 py-3 flex items-center gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <p className="text-[16px] font-medium text-[#030712]">
                      Between sessions
                    </p>
                    <p className="text-[14px] text-[#4a5567]">
                      {nextTitle
                        ? `Up next: ${nextTitle}`
                        : 'Nothing left in the running order. The room stays open.'}
                    </p>
                  </div>
                  {isHost && room?.next_id && (
                    <button
                      onClick={() => startSession()}
                      disabled={starting}
                      className="bg-navy-800 hover:bg-navy-700 disabled:opacity-60
                        text-white rounded-[8px] px-4 py-2 text-[13px] font-medium"
                    >
                      {starting ? 'Starting…' : `Start ${nextTitle}`}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* What is being said */}
            <div className="border-t border-[#e3e8ef]">
              <div className="bg-white border-b border-[#e3e8ef] px-4 py-2.5">
                <h2 className="text-[20px] font-medium text-black text-center leading-[1.2]">
                  Live Transcript
                </h2>
              </div>
              <div className="flex flex-col gap-3 px-3 py-2.5 max-h-[420px] overflow-y-auto">
                {roomLines.length === 0 ? (
                  <p className="text-[14px] text-[#656565]">
                    Lines appear here once the hall device starts sending them.
                  </p>
                ) : (
                  roomLines.map((seg, idx) => (
                    <div key={idx} className="flex gap-3 items-start">
                      <span className="border border-[#e3e8ef] rounded-[4px] h-6 px-1 grid
                        place-items-center flex-none text-[12px] text-[#656565]
                        tracking-[-0.06px] tabular-nums">
                        {seg.created_at
                          ? new Date(seg.created_at).toLocaleTimeString([], {
                              hour: '2-digit', minute: '2-digit',
                            })
                          : formatElapsed(Math.round(seg.start_time))}
                      </span>
                      <span aria-hidden className="w-px h-3 bg-[#e3e8ef] mt-1.5 flex-none" />
                      <p
                        data-transcript-line
                        className="flex-1 min-w-0 text-[14px] leading-[1.4] tracking-[-0.07px]
                          text-[#383838]"
                      >
                        {seg.speaker_name && (
                          <span className="text-[#4a5567]">{seg.speaker_name}: </span>
                        )}
                        {seg.text}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </RoomCard>
        </div>

        {/* Whatever the bar has been asked for, in the order it was asked.
            Only these three may sit here, and until one is asked for the
            transcript keeps the width to itself. */}
        {side.length > 0 && (
          <div className="flex flex-col gap-3 min-w-0">
            {side.map((panel) => (
              <React.Fragment key={panel}>
                {panel === 'resources' && (
          <RoomCard>
            <SidePanelHead title="Resources" onClose={() => closeSide('resources')} />

            <div className="bg-[#fcfcfc] flex border-b border-[#e3e8ef]" role="tablist">
              {(['slides', 'photos'] as const).map((which) => (
                <button
                  key={which}
                  role="tab"
                  aria-selected={resourceTab === which}
                  onClick={() => setResourceTab(which)}
                  className={`flex-1 py-2 text-[14px] capitalize transition-colors ${
                    resourceTab === which
                      ? 'text-black font-medium border-b-2 border-black'
                      : 'text-[#49454f] hover:text-black'
                  }`}
                >
                  {which}
                </button>
              ))}
            </div>

            {resourceTab === 'slides' && (
              <div className="flex justify-end px-4 pt-2">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadPercent !== null}
                  className="text-[13px] px-3 py-1.5 rounded-lg bg-navy-800 hover:bg-navy-700
                    text-white disabled:opacity-50"
                >
                  {uploadPercent !== null ? `${uploadPercent}%` : '+ Upload'}
                </button>
              </div>
            )}

            <input ref={fileInputRef} type="file" onChange={handleUpload} className="hidden" />

            {uploadPercent !== null && (
              <div className="h-1 bg-[#e3e8ef] overflow-hidden">
                <div
                  className="h-full bg-navy-700 transition-all"
                  style={{ width: `${uploadPercent}%` }}
                />
              </div>
            )}

            {resourceTab === 'photos' ? (
              <div className="px-4 py-3">
                <PhotoUploads eventRef={eventCode ?? ''} />
              </div>
            ) : (
            <div className="max-h-[210px] overflow-y-auto overscroll-contain">
              {resources.length === 0 ? (
                <p className="text-[14px] text-[#656565] px-4 py-3">
                  No files yet. Uploads are saved to the host's Google Drive and shared with
                  everyone here.
                </p>
              ) : (
                resources.map((r) => (
                  <div key={r.id} className="mx-2 my-1 flex items-center gap-2
                    rounded-[12px] border border-[#e3e8ef] bg-white px-4 py-2.5">
                    <a
                      href={r.web_view_link ?? '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex min-w-0 flex-1 items-center gap-4
                        hover:opacity-80"
                    >
                      <KindChip kind={kindOf(r.display_name ?? '')} />
                      <span className="min-w-0">
                        <span className="block text-[14px] font-medium text-[#101828]
                          tracking-[-0.07px] truncate">
                          {r.display_name}
                        </span>
                        <span className="block text-[12px] text-[#6a7282] truncate">
                          {formatFileSize(r.file_size)}
                          {r.metadata?.uploaded_by_email &&
                            ` · ${
                              r.metadata.uploaded_by_email === user?.email
                                ? 'you'
                                : r.metadata.uploaded_by_email
                            }`}
                          {r.is_released === false && ' · not open to the room yet'}
                        </span>
                      </span>
                    </a>
                    {canOrganize && eventId && (
                      <button
                        type="button"
                        disabled={deletingResource === r.id}
                        onClick={() => handleResourceDelete(r)}
                        aria-label={`Delete ${r.display_name}`}
                        className="size-10 grid flex-none place-items-center rounded-[10px]
                          bg-[#f3f4f6] text-[#d81313] hover:bg-[#fee2e2]
                          disabled:cursor-wait disabled:opacity-50"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24"
                          fill="none" stroke="currentColor" strokeWidth="1.8"
                          strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 11v5M14 11v5" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
            )}
          </RoomCard>

                )}
                {panel === 'participants' && (
          <RoomCard>
            <SidePanelHead
              title="Participants"
              onClose={() => closeSide('participants')}
            />

            <div className="flex border-b border-[#e3e8ef]" role="tablist">
              {([
                ['here', `In the room (${participants.length})`],
                ['attendance', 'Attendance'],
              ] as const).map(([key, label]) => (
                <button
                  key={key}
                  role="tab"
                  aria-selected={peopleTab === key}
                  onClick={() => {
                    setPeopleTab(key);
                    if (key === 'attendance') loadAttendance();
                  }}
                  className={`flex-1 py-2 text-[13px] font-medium transition ${
                    peopleTab === key
                      ? 'text-black border-b-2 border-black'
                      : 'text-[#49454f] hover:text-black'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="max-h-[460px] overflow-y-auto">
              {peopleTab === 'here' ? (
                (participants as EventParticipant[]).length === 0 ? (
                  <p className="text-[14px] text-[#656565] px-4 py-3">
                    Nobody is here yet.
                  </p>
                ) : (
                  (participants as EventParticipant[]).map((p) => {
                    const isMe = p.user.id === user?.id;
                    return (
                      <div
                        key={p.id}
                        className="flex items-center gap-3 px-3 py-2.5 border-b
                          border-[#e3e8ef] last:border-0"
                      >
                        <RoomPortrait name={p.user.email} size={36} />
                        <div className="min-w-0 flex-1">
                          <p className="text-[14px] font-medium text-black truncate">
                            {p.user.email}
                            {isMe && (
                              <span className="text-[#656565] font-normal"> (you)</span>
                            )}
                          </p>
                          <p className="text-[12px] text-[#656565] uppercase tracking-wide">
                            {p.role.replace('_', '-')}
                            {p.is_muted && ' · muted'}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )
              ) : !attendance ? (
                <p className="text-[14px] text-[#656565] px-4 py-3">Loading…</p>
              ) : (
                <div className="p-3">
                  <div className="grid grid-cols-2 gap-2 text-center">
                    {[
                      ['On the roll', attendance.expected_total],
                      ['Attended', attendance.attended_count],
                      ['In event now', attendance.active_count],
                      ['Absent', attendance.absent_count],
                    ].map(([label, value]) => (
                      <div
                        key={label as string}
                        className="bg-[#fcfcfc] border border-[#e3e8ef] rounded-lg p-2"
                      >
                        <p className="text-[20px] font-semibold leading-tight">
                          {value as number}
                        </p>
                        <p className="text-[11px] text-[#656565]">{label as string}</p>
                      </div>
                    ))}
                  </div>

                  <h3 className="font-semibold text-[13px] mt-4 mb-1.5">
                    Came ({attendance.attended.length})
                  </h3>
                  <div className="flex flex-col">
                    {attendance.attended.map((a, i) => (
                      <div
                        key={`${a.type}-${i}`}
                        className="flex items-center gap-2.5 py-1.5 border-b
                          border-[#e3e8ef] last:border-0"
                      >
                        <RoomPortrait name={a.name} size={28} />
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] truncate">{a.name}</p>
                          <p className="text-[11px] text-[#656565] truncate">
                            {a.email ?? a.role}
                          </p>
                        </div>
                        <span
                          className={`text-[11px] rounded-full px-2 py-0.5 flex-none ${
                            a.is_active
                              ? 'bg-ok/[.12] text-ok'
                              : 'bg-navy-800/[.07] text-[#656565]'
                          }`}
                        >
                          {a.is_active ? 'in the room' : 'left'}
                        </span>
                      </div>
                    ))}
                  </div>

                  {attendance.did_not_attend.length > 0 && (
                    <>
                      <h3 className="font-semibold text-[13px] mt-4 mb-1.5">
                        Did not come ({attendance.did_not_attend.length})
                      </h3>
                      {attendance.did_not_attend.map((a) => (
                        <p
                          key={a.email}
                          className="text-[13px] text-[#656565] py-1 border-b
                            border-[#e3e8ef] last:border-0"
                        >
                          {a.email}
                        </p>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </RoomCard>
                )}
                {panel === 'requests' && isHost && (
                  <RoomCard>
                    <SidePanelHead
                      title="Requests"
                      badge={waitingGuests.length + boardRequests.length}
                      onClose={() => closeSide('requests')}
                    />
                    <div className="flex border-b border-[#e3e8ef]" role="tablist">
                      {([
                        ['room', 'Room'],
                        ['board', 'Board'],
                      ] as const).map(([key, label]) => (
                        <button
                          key={key}
                          role="tab"
                          aria-selected={requestTab === key}
                          onClick={() => setRequestTab(key)}
                          className={`flex-1 py-2 text-[13px] font-medium transition ${
                            requestTab === key
                              ? 'text-black border-b-2 border-black'
                              : 'text-[#49454f] hover:text-black'
                          }`}
                        >
                          {label}
                          {key === 'room' && waitingGuests.length > 0
                            ? ` (${waitingGuests.length})`
                            : key === 'board' && boardRequests.length > 0
                            ? ` (${boardRequests.length})`
                            : ''}
                        </button>
                      ))}
                    </div>
                    <div className="max-h-[460px] overflow-y-auto">
                      {requestTab === 'room' ? (
                        waitingGuests.length === 0 ? (
                          <p className="px-4 py-3 text-[13px] text-[#656565]">
                            No guest join requests.
                          </p>
                        ) : waitingGuests.map((guest) => (
                          <RequestRow
                            key={guest.id}
                            name={guest.full_name}
                            under="Joining as a guest"
                            actions={(
                              <>
                                <RequestButton
                                  tone="accept"
                                  disabled={decidingGuest === guest.id}
                                  onClick={() => decideGuest(guest.id, 'admit')}
                                >
                                  Accept
                                </RequestButton>
                                <RequestButton
                                  tone="decline"
                                  disabled={decidingGuest === guest.id}
                                  onClick={() => decideGuest(guest.id, 'deny')}
                                >
                                  Reject
                                </RequestButton>
                              </>
                            )}
                          />
                        ))
                      ) : boardRequests.length === 0 ? (
                        <p className="px-4 py-3 text-[13px] text-[#656565]">
                          No question or suggestion requests.
                        </p>
                      ) : boardRequests.map((message) => (
                        <RequestRow
                          key={message.id}
                          /* Whole, not truncated: this is the thing being
                             decided on, so it has to be readable. */
                          stacked
                          name={message.body}
                          under={`${message.sender_name} · ${
                            message.topic === 'suggestion' ? 'Suggestion' : 'Question'
                          }`}
                          actions={(
                            <>
                              <RequestButton
                                tone="accept"
                                disabled={decidingGuest === message.id}
                                onClick={() => decideBoardRequest(message, 'approve')}
                              >
                                Accept
                              </RequestButton>
                              <RequestButton
                                tone="quiet"
                                disabled={decidingGuest === message.id}
                                onClick={() => decideBoardRequest(message, 'decline')}
                              >
                                Reject
                              </RequestButton>
                            </>
                          )}
                        />
                      ))}
                    </div>
                  </RoomCard>
                )}
                {panel === 'questions' && (
          <RoomCard>
            <SidePanelHead title="Questions" onClose={() => closeSide('questions')} />
            {eventId && (
              <RoomQuestions
                eventId={eventId}
                agendaSessionId={currentEvent?.current_session?.id ?? null}
                refreshMs={20000}
                canSort={canOrganize}
                showRequests={false}
                canVote={!canOrganize}
                onNews={(many) => noteUnseen('questions', many)}
                onAsk={canOrganize ? undefined : (body, topic) => askHost(body, topic)}
              />
            )}
          </RoomCard>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* Centred on the bar, with leaving out of the way at the end of
          it: a right-hand button that takes part in the centring drags
          everything else off centre, which is what it had been doing. */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 bg-navy-800 px-4 py-3"
        aria-label="Event controls"
      >
        <div className="flex items-center justify-center gap-2 sm:gap-[38px] overflow-x-auto">
          <RoomBarButton
            icon="participants"
            label={`Participants${participants.length ? ` (${participants.length})` : ''}`}
            open={side.includes('participants')}
            onClick={() => toggleSide('participants')}
          />
          {isHost && (
            <RoomBarButton
              icon="questions"
              label="Requests"
              badge={waitingGuests.length + boardRequests.length}
              open={side.includes('requests')}
              onClick={() => {
                toggleSide('requests');
              }}
            />
          )}
          <RoomBarButton
            icon="questions"
            label="Questions"
            badge={unseen.questions}
            open={side.includes('questions')}
            onClick={() => toggleSide('questions')}
          />
          <RoomBarButton
            icon="resources"
            label="Resources"
            badge={unseen.resources}
            open={side.includes('resources')}
            onClick={() => toggleSide('resources')}
          />
          <RoomBarButton icon="share" label="Share" onClick={() => setShowShare(true)} />
        </div>

        <span className="absolute end-4 top-1/2 -translate-y-1/2 hidden sm:block">
          <RoomBarButton
            icon="leave"
            label="Leave"
            tone="leave"
            // The host is always asked. It used to depend on something
            // being on stage, which left them no way to end the event
            // from inside the room between two talks - and the room lives
            // between two talks now.
            onClick={() => (isHost ? setShowEndChoice(true) : leaveEvent())}
          />
        </span>
      </nav>
    </div>
  );
};

/**
 * The room, with the reader's language and accessibility settings.
 *
 * It shares components with the dashboards - the resource controls, the
 * photo section - and those speak both languages, which means they need
 * the same context every other screen gives them. Without it they throw
 * on first render, and the whole room goes down with them.
 */
export const EventRoomPage: React.FC = () => (
  <OrganizerProvider>
    <EventRoomInner />
  </OrganizerProvider>
);
